import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, ContentPanel, Loader } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';

import {
  analyticsEditCgRiderAlerts,
  analyticsEditedAccountSettings,
  analyticsInputAbsentAlertEmailAddress,
  analyticsPage,
  analyticsToggledAbsentEmailAlert,
} from '../../../api/services/Analytics';
import { actions, Can, subjects } from '../../../contexts/AbilityContext';

import AccountInfo from './AccountInfo';
import NotificationPreferences from './NotificationPreferences';
import Permissions from './Permissions';
import RidePreferences from './RiderPreferences';
import { useSettingsForm } from './useSettingsForm';
import { settingsSchema } from './validationSchema';

const SettingsForm: React.FC = () => {
  const { accountInfo, editMode, editModeToggle, saveSettings } = useSettingsForm();
  const methods = useForm<AccountInfo>({
    defaultValues: useMemo(() => {
      return accountInfo;
    }, [accountInfo]),
    resolver: yupResolver(settingsSchema()),
  });

  useEffect(() => {
    methods.reset(accountInfo);
  }, [accountInfo]);

  const handleSubmit = (data, e) => {
    e.preventDefault();
    saveSettings(data);
    editModeToggle();

    // Analytics
    analyticsEditedAccountSettings();
    analyticsEditCgRiderAlerts(data?.messagingUpdatesRidersCaregivers);

    if (data?.enableAbsentRiderDailyEmail === 'Yes') {
      analyticsInputAbsentAlertEmailAddress(data.dailyReportEmailList);
      analyticsToggledAbsentEmailAlert(data.sendEmailDailyReport);
    }
  };

  useEffect(() => {
    analyticsPage(editMode ? 'Edit Account Settings' : 'Account Settings');
  }, [editMode]);

  return (
    <>
      <div data-testid="settings-actions" style={{ gridArea: 'actions' }}>
        {editMode ? (
          <Box ml="auto" sx={{ textAlign: 'right' }}>
            <Button color="tertiary" size="medium" onClick={editModeToggle}>
              Cancel
            </Button>
            <Button form="settings-form" color="primary" size="medium" type="submit">
              Save
            </Button>
          </Box>
        ) : (
          <Box ml="auto" sx={{ textAlign: 'right' }}>
            <Can I={actions.edit} a={subjects.settings}>
              <Button color="primary" size="medium" onClick={editModeToggle}>
                Edit
              </Button>
            </Can>
          </Box>
        )}
      </div>
      <div style={{ gridArea: 'content', marginTop: '1.5rem' }}>
        {accountInfo ? (
          <FormProvider {...methods}>
            <form id="settings-form" onSubmit={methods.handleSubmit(handleSubmit)}>
              <ContentPanel>
                <AccountInfo accountInfo={accountInfo} editMode={editMode} />
              </ContentPanel>
              <ContentPanel mt={2}>
                <RidePreferences accountInfo={accountInfo} editMode={editMode} />
              </ContentPanel>
              <ContentPanel mt={2}>
                <Permissions accountInfo={accountInfo} editMode={editMode} />
              </ContentPanel>
              <ContentPanel mt={2}>
                <NotificationPreferences accountInfo={accountInfo} editMode={editMode} />
              </ContentPanel>
            </form>
          </FormProvider>
        ) : (
          <Loader />
        )}
      </div>
    </>
  );
};

export default SettingsForm;
